import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Printer } from "lucide-react";
import { usd } from "@/lib/auth";
import { LogoMark } from "@/components/layout/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Proposal() {
  const { quoteId } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/proposals/${quoteId}`)
      .then((r) => setData(r.data))
      .catch(() => setMissing(true));
  }, [quoteId]);

  if (missing) {
    return (
      <main className="min-h-screen bg-void flex items-center justify-center px-6" data-testid="proposal-missing">
        <p className="text-white/50 text-sm text-center">This proposal is unavailable. Please contact Fairlume for an updated link.</p>
      </main>
    );
  }
  if (!data) return <main className="min-h-screen bg-void" />;

  const validUntil = data.valid_until
    ? new Date(data.valid_until).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : null;

  return (
    <main className="min-h-screen bg-void py-14 px-4 sm:px-8" data-testid="proposal-page">
      <div className="max-w-3xl mx-auto bg-card-dark border hairline rounded-md overflow-hidden">
        <div className="p-8 sm:p-12 border-b hairline flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-6"><LogoMark /><span className="font-display font-bold tracking-[0.18em] text-sm">FAIRLUME</span></div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">Lighting Proposal</h1>
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/35 mt-2">{data.quote_id}</p>
          </div>
          <button onClick={() => window.print()} data-testid="proposal-print-btn" className="inline-flex items-center gap-2 text-xs text-white/50 hover:text-white border hairline rounded-sm px-4 py-2.5 transition-colors print:hidden">
            <Printer size={13} /> Print / PDF
          </button>
        </div>

        <div className="p-8 sm:p-12">
          <div className="grid grid-cols-2 gap-8 mb-10">
            <div>
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#A7D7A0] mb-3">Prepared For</p>
              <p className="text-white font-medium" data-testid="proposal-company">{data.prepared_for.company}</p>
              <p className="text-white/60 text-sm">{data.prepared_for.name}</p>
              <p className="text-white/40 text-sm">{data.prepared_for.location}</p>
            </div>
            <div className="text-right">
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#A7D7A0] mb-3">Prepared By</p>
              <p className="text-white font-medium">{data.contact.company}</p>
              <p className="text-white/60 text-sm">{data.contact.location}</p>
              <p className="text-white/40 text-sm">{data.contact.email}</p>
              <p className="text-white/40 text-sm">{data.contact.phone}</p>
            </div>
          </div>

          <table className="w-full text-sm mb-8">
            <thead>
              <tr className="border-b hairline text-left">
                <th className="py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/40">Item</th>
                <th className="py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/40 text-center">Qty</th>
                <th className="py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/40 text-right">Unit</th>
                <th className="py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/40 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((l, i) => (
                <tr key={i} className="border-b hairline" data-testid={`proposal-line-${i}`}>
                  <td className="py-4 pr-4">
                    <p className="text-white">{l.product}</p>
                    <p className="text-white/40 text-xs mt-0.5">
                      {l.model_number}{l.warranty ? ` · ${l.warranty} warranty` : ""}{l.lead_time_days ? ` · ~${l.lead_time_days}-day lead time` : ""}
                    </p>
                  </td>
                  <td className="py-4 text-center text-white/70">{l.qty}</td>
                  <td className="py-4 text-right text-white/70">{usd(l.unit_price)}</td>
                  <td className="py-4 text-right text-white">{usd(l.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-end mb-10">
            <div className="text-right">
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1">Project Total</p>
              <p className="font-display font-bold text-3xl text-[#A7D7A0]" data-testid="proposal-total">{usd(data.total)}</p>
            </div>
          </div>

          {data.notes && (
            <div className="mb-10">
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#A7D7A0] mb-3">Scope Notes</p>
              <p className="text-white/70 text-sm leading-relaxed whitespace-pre-line" data-testid="proposal-notes">{data.notes}</p>
            </div>
          )}

          <div className="border-t hairline pt-8 text-xs text-white/40 leading-relaxed space-y-2">
            {validUntil && <p>This estimate is valid until {validUntil}.</p>}
            <p>This proposal is an estimate, not a binding contract. Final pricing is confirmed in a written agreement following site assessment.</p>
            <p>Installation is performed by independent, licensed installation contractors under their own agreements, licenses, and insurance. Fairlume LLC sources lighting systems and coordinates the project but does not perform installation work.</p>
            <p className="pt-3 text-white/50">Questions? {data.contact.email} · {data.contact.phone}</p>
          </div>
        </div>
      </div>
    </main>
  );
}
